import React from 'react';

interface Props {
  children: React.ReactNode;
}

interface State {
  hasError: boolean;
}

const RELOAD_FLAG = 'uhci_chunk_reload_attempted';

function isChunkLoadError(error: unknown): boolean {
  const msg = error instanceof Error ? error.message : String(error);
  return msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('ChunkLoadError') ||
    /Loading chunk \d+ failed/.test(msg);
}

/**
 * Catches stale-deployment chunk load failures and reloads once to pick up new assets.
 */
export class ChunkErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error): void {
    if (!isChunkLoadError(error)) return;

    const alreadyRetried = sessionStorage.getItem(RELOAD_FLAG) === '1';
    if (alreadyRetried) return;

    sessionStorage.setItem(RELOAD_FLAG, '1');
    window.location.reload();
  }

  render() {
    if (this.state.hasError) {
      const alreadyRetried = sessionStorage.getItem(RELOAD_FLAG) === '1';
      return (
        <div style={{ padding: '32px', textAlign: 'center', color: '#2d2d39' }}>
          <p style={{ fontSize: '16px', fontWeight: 600 }}>
            {alreadyRetried
              ? 'This page failed to load. Please refresh your browser.'
              : 'Loading\u2026'}
          </p>
          {alreadyRetried && (
            <button
              onClick={() => {
                sessionStorage.removeItem(RELOAD_FLAG);
                window.location.reload();
              }}
              style={{
                marginTop: '12px',
                padding: '8px 20px',
                background: '#196ecf',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px',
              }}
            >
              Retry
            </button>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ChunkErrorBoundary;
